import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, ButtonBase, Chip, CircularProgress, Link, Typography } from "@mui/material";
import {
  RiArrowRightSLine,
  RiCalendarCheckLine,
  RiCalendarLine,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiCopperCoinLine,
  RiDoubleQuotesL,
  RiFlagLine,
  RiGlobalLine,
  RiHourglassLine,
  RiLoginBoxLine,
  RiMailLine,
  RiPhoneLine,
  RiPlayCircleLine,
  RiStarFill,
  RiStarLine,
  RiStickyNoteLine,
  RiTicketLine,
  RiUserLine,
} from "@remixicon/react";
import { dayLabel, money, timeLabel, whenLabel } from "../../utils/queuePresentation";
import { childPath, endOf } from "../../utils/lessonPresentation";
import { personName, sessionsLabel } from "../../utils/studentDetail";
import { reviewsLine } from "../../utils/tutorDetail";
import { ratingText } from "../../utils/tutorPresentation";
import { toDate } from "../../utils/ticketPresentation";
import {
  activityEvents,
  creditTile,
  lessonRatings,
  otherRatings,
  ratingWord,
  statusLine,
  subjectText,
  tutorPay,
  walletMove,
} from "../../utils/lessonDetail";
import { tokens } from "../../utils/figmaTokens";
import DetailRows from "../../components/kit/DetailRows";
import InitialsAvatar from "../../components/kit/InitialsAvatar";
import SmallButton from "../../components/kit/SmallButton";
import StatStrip from "../../components/kit/StatStrip";
import { EmptyState, ErrorState } from "../../components/kit/States";
import { ArrowLink, Panel, PanelHead } from "../Inbox/TicketParts";

// The lesson page's four tabs (047 D100–D103), drawn from Figma's Completed lesson frames
// (Participants 11476:83440, Activity 11465:78752, Payments 11477:85131, Rating 11478:86891).
// Everything is stored data, in the reader's time zone. `records` is getLessonRecords' answer
// with `loading` and `error`; `nameOf(uid)` is a person's name once their document is read.

const TONES = {
  success: tokens.success,
  warning: tokens.warning,
  error: tokens.error,
  info: tokens.info,
  brand: { main: tokens.icon.brand, ink: tokens.brand.text, bg: tokens.brand.subtle, border: tokens.brand.subtle },
  default: tokens.neutral,
  neutral: tokens.neutral,
};
const rowLook = { labelColor: tokens.text.secondary, line: tokens.divider };

const Loading = () => (
  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
    <CircularProgress size={22} />
  </Box>
);

// A section's head outside a card: the title and a grey line.
const SectionHead = ({ title, line }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
    <Typography variant="titleSection" component="h2" sx={{ color: tokens.text.primary }}>
      {title}
    </Typography>
    {line && (
      <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
        {line}
      </Typography>
    )}
  </Box>
);

// A quoted text: a written note or feedback.
const Quote = ({ text }) => (
  <Box sx={{ display: "flex", gap: "10px", p: "16px", borderRadius: "12px", bgcolor: tokens.info.bg }}>
    <Box aria-hidden sx={{ color: tokens.icon.brand, flexShrink: 0 }}>
      <RiDoubleQuotesL size={18} />
    </Box>
    <Typography variant="bodyMedium" sx={{ color: tokens.text.primary, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
      {text}
    </Typography>
  </Box>
);

// ── Participants (D100) ──

const STATUS_ICONS = {
  Completed: RiCheckboxCircleLine,
  Cancelled: RiCloseCircleLine,
  Reported: RiFlagLine,
  "Live now": RiPlayCircleLine,
  Upcoming: RiCalendarLine,
  "Awaiting outcome": RiHourglassLine,
};

// The status line: what happened, in the chip's tone, with the ticket when there is one.
const StatusBanner = ({ line }) => {
  const tone = TONES[line.chip.color] || TONES.default;
  const Icon = STATUS_ICONS[line.chip.label] || RiCalendarLine;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "12px",
        p: "16px",
        borderRadius: "12px",
        bgcolor: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    >
      <Box aria-hidden sx={{ display: "flex", p: "8px", borderRadius: "999px", bgcolor: tokens.surface.default, color: tone.main }}>
        <Icon size={18} />
      </Box>
      <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
        <Typography variant="bodyMediumStrong" component="p" sx={{ color: tone.ink }}>
          {line.title}
        </Typography>
        {line.detail && (
          <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
            {line.detail}
          </Typography>
        )}
      </Box>
      {line.ticketTo && (
        <SmallButton tone="outline" Icon={RiTicketLine} component={RouterLink} to={line.ticketTo}>
          Open the ticket
        </SmallButton>
      )}
    </Box>
  );
};

// One contact cell under a person: a ringed icon, a small label and the value.
const ContactCell = ({ Icon, label, value }) => (
  <Box sx={{ flex: "1 1 180px", minWidth: 0, display: "flex", alignItems: "center", gap: "10px", px: "12px", py: "10px" }}>
    <Box aria-hidden sx={{ display: "flex", p: "7px", borderRadius: "999px", bgcolor: tokens.surface.default, border: `1px solid ${tokens.border.default}`, color: tokens.icon.secondary }}>
      <Icon size={16} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="labelMicro" component="div" sx={{ color: tokens.text.tertiary }}>
        {label}
      </Typography>
      <Typography variant="bodySmall" sx={{ color: tokens.text.primary, overflowWrap: "anywhere" }}>
        {value || "—"}
      </Typography>
    </Box>
  </Box>
);

// A learner or tutor card: the name and a line, "Joined 16:59" when the app recorded the Join
// press, their contact cells and the link to their page.
const PersonBlock = ({ person }) => (
  <Panel strong>
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
      <InitialsAvatar name={person.name} colorKey={person.colorKey} size={40} square />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="titleCard" component="h3" sx={{ color: tokens.text.primary, overflowWrap: "anywhere" }}>
          {person.name}
        </Typography>
        <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
          {person.line}
        </Typography>
      </Box>
      {person.joined && <Chip label={`Joined ${person.joined}`} color="success" />}
    </Box>
    <Box sx={{ display: "flex", flexWrap: "wrap", borderRadius: "10px", bgcolor: tokens.surface.sunken, border: `1px solid ${tokens.border.subtle}` }}>
      <ContactCell Icon={RiUserLine} label="Role" value={person.role} />
      <ContactCell Icon={RiMailLine} label="Email" value={person.email} />
      <ContactCell Icon={RiPhoneLine} label="Phone" value={person.phone} />
      <ContactCell Icon={RiGlobalLine} label="Time zone" value={person.zone} />
    </Box>
    {person.to && <ArrowLink to={person.to}>{person.linkLabel}</ArrowLink>}
  </Panel>
);

export const ParticipantsTab = ({ booking, users, childName, nameOf, timeZone, can }) => {
  const student = users[booking.studentId];
  const tutor = users[booking.teacherId];
  const parent = personName(student) || "—";
  const child = childPath(booking);
  const joined = (at) => (toDate(at) ? timeLabel(at, { timeZone }) : null);
  const end = endOf(booking);
  const people = [
    {
      key: "learner",
      name: child ? childName || "Child" : parent,
      colorKey: child ? `${child.parentId}/${child.childId}` : booking.studentId,
      line: child ? `Child · Parent: ${parent}` : "Student",
      role: "Learner",
      email: student?.email,
      phone: student?.phone || student?.phoneNumber,
      zone: booking.studentTimeZone || student?.timeZone,
      joined: joined(booking.studentJoinedAt),
      to: can.students ? `/students/${booking.studentId}` : null,
      linkLabel: "Open student page",
    },
    {
      key: "tutor",
      name: personName(tutor) || "—",
      colorKey: booking.teacherId,
      line: tutor ? reviewsLine(tutor) : "Tutor",
      role: "Tutor",
      email: tutor?.email,
      phone: tutor?.phone || tutor?.phoneNumber,
      zone: tutor?.timeZone,
      joined: joined(booking.teacherJoinedAt),
      to: can.tutors ? `/tutors/${booking.teacherId}` : null,
      linkLabel: "Open tutor page",
    },
  ];
  const left = [
    { key: "id", label: "Lesson ID", value: booking.id },
    { key: "subject", label: "Subject", value: subjectText(booking.subject) || "—" },
    {
      key: "scheduled",
      label: "Scheduled",
      value: toDate(booking.startTimeUTC) ? `${whenLabel(booking.startTimeUTC, { timeZone })}${end ? ` – ${timeLabel(end, { timeZone })}` : ""}` : "—",
    },
  ];
  const right = [
    { key: "booked", label: "Booked at", value: toDate(booking.createdAt) ? whenLabel(booking.createdAt, { timeZone }) : "—" },
    { key: "by", label: "Booked by", value: parent },
    {
      key: "link",
      label: "Class link",
      value: booking.classLink ? (
        <Link href={booking.classLink} target="_blank" rel="noopener noreferrer">
          Open meeting
        </Link>
      ) : (
        "—"
      ),
    },
    { key: "trial", label: "Free trial", value: booking.isFreeSession ? "Yes" : "No" },
    // Old bookings can carry the learner's notes; the drawer showed them (D3).
    ...(booking.notes ? [{ key: "notes", label: "Notes", value: booking.notes }] : []),
  ];
  const note = booking.parentNote;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <StatusBanner line={statusLine(booking, { nameOf, timeZone })} />
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <SectionHead title="Participants (2)" line="The learner and the tutor of this lesson." />
        {people.map((p) => (
          <PersonBlock key={p.key} person={p} />
        ))}
      </Box>
      <Panel strong>
        <PanelHead title="Lesson details" line="What was booked, and when." />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 4 }}>
          <DetailRows {...rowLook} rows={left} />
          <DetailRows {...rowLook} rows={right} />
        </Box>
      </Panel>
      {note?.text && (
        <Panel strong>
          <PanelHead
            title="Note to the parent"
            line={[
              `Written by ${personName(tutor) || "the tutor"}${toDate(note.createdAt) ? ` · ${whenLabel(note.createdAt, { timeZone })}` : ""}`,
              toDate(note.seenAt) ? `Seen by the family ${whenLabel(note.seenAt, { timeZone })}` : "Not seen yet",
            ].join(" · ")}
          />
          <Quote text={note.text} />
        </Panel>
      )}
    </Box>
  );
};

// ── Activity (D101) ──

const EVENT_ICONS = {
  booked: RiCalendarCheckLine,
  wallet: RiCopperCoinLine,
  joined: RiLoginBoxLine,
  completed: RiCheckboxCircleLine,
  cancelled: RiCloseCircleLine,
  reported: RiFlagLine,
  ticket: RiTicketLine,
  rated: RiStarLine,
  note: RiStickyNoteLine,
};

export const ActivityTab = ({ booking, records, nameOf, learner, timeZone, onRetry }) => {
  if (records.loading) return <Loading />;
  if (records.error) return <ErrorState title="Could not load the activity" message={records.error} onRetry={onRetry} compact />;
  const events = activityEvents(booking, { ...records, nameOf, learner, timeZone });
  const days = [];
  events.forEach((e) => {
    const last = days[days.length - 1];
    if (last && last.day === e.day) last.events.push(e);
    else days.push({ day: e.day, events: [e] });
  });
  return (
    <Panel strong gap="16px">
      <PanelHead title="Activity" line="Every stored event with a time, oldest first." />
      {days.length === 0 && <EmptyState title="No events recorded." compact />}
      {days.map((d) => (
        <Box key={d.day} component="section" aria-label={d.day}>
          <Typography variant="bodyMediumStrong" component="h3" sx={{ borderLeft: `2px solid ${tokens.border.strong}`, pl: "10px", color: tokens.text.primary }}>
            {d.day}
          </Typography>
          <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none" }}>
            {d.events.map((e) => {
              const tone = TONES[e.tone] || TONES.neutral;
              const Icon = EVENT_ICONS[e.icon] || RiCalendarLine;
              return (
                <Box component="li" key={e.key} sx={{ display: "flex", alignItems: "flex-start", gap: "12px", py: "10px" }}>
                  <Box aria-hidden sx={{ display: "flex", p: "7px", borderRadius: "999px", bgcolor: tone.bg, color: tone.main, flexShrink: 0 }}>
                    <Icon size={15} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="bodySmall" sx={{ color: tokens.text.primary }}>
                      {e.title}
                    </Typography>
                    {e.sub && (
                      <Typography variant="labelMedium" component="div" sx={{ color: tokens.text.tertiary }}>
                        {e.sub}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary, whiteSpace: "nowrap" }}>
                    {e.time}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Panel>
  );
};

// ── Payments (D102) ──

export const PaymentsTab = ({ booking, records, users, versions, nameOf, timeZone, can, onRetry }) => {
  const student = users[booking.studentId];
  const tutor = users[booking.teacherId];
  const credit = creditTile(booking);
  const at = (value) => (toDate(value) ? `${dayLabel(value, { timeZone })}, ${timeLabel(value, { timeZone })}` : "");
  const charge = records.walletRows.find((r) => r.bookingId === booking.id);
  const moves = [...records.walletRows].sort((a, b) => (toDate(a.createdAt)?.getTime() || 0) - (toDate(b.createdAt)?.getTime() || 0));
  const pay = tutorPay(booking, tutor, versions);
  const balance =
    charge && typeof charge.previousBalance === "number" && typeof charge.newBalance === "number"
      ? `${money(charge.previousBalance)} → ${money(charge.newBalance)}`
      : booking.isFreeSession
        ? "No credit moved"
        : "—";
  const paid = [
    { key: "with", label: "Paid with", value: credit.value },
    { key: "credit", label: "The credit", value: credit.caption },
    { key: "balance", label: "Balance", value: records.loading ? "…" : balance },
    { key: "at", label: "Charged at", value: records.loading ? "…" : charge ? at(charge.createdAt) || "—" : "—" },
  ];
  const payRows = pay && [
    { key: "hours", label: "Hours", value: `${pay.hours} h` },
    { key: "rate", label: "Rate", value: pay.rate === null ? "No rate set" : `E£${money(pay.rate)} an hour` },
    { key: "amount", label: "Pay for this lesson", value: pay.amount === null ? "—" : `E£${money(pay.amount)}` },
    { key: "cycle", label: "Pay cycle", value: pay.cycle || "—" },
  ];
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, alignItems: "start" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
        <Panel strong>
          <PanelHead title="How it was paid" line="What this lesson took from the wallet." />
          <DetailRows {...rowLook} rows={paid} />
        </Panel>
        <Panel strong>
          <PanelHead title="Tutor's pay" line={personName(tutor) || undefined} />
          {payRows ? (
            <DetailRows {...rowLook} rows={payRows} />
          ) : (
            <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
              {booking.status === "Pending" ? "The tutor is paid once the lesson is completed." : "This lesson does not pay the tutor."}
            </Typography>
          )}
        </Panel>
        <Panel strong>
          <PanelHead
            title="Family wallet"
            line={personName(student) || undefined}
            aside={
              can.students && (
                <SmallButton tone="outline" component={RouterLink} to={`/students/${booking.studentId}/wallet`}>
                  Open wallet
                </SmallButton>
              )
            }
          />
          <StatStrip items={[{ key: "balance", value: student ? sessionsLabel(student.WalletBalance) : "—", label: "Balance now" }]} />
        </Panel>
      </Box>
      <Panel strong>
        <PanelHead title="Credit movement" line="Every wallet row about this lesson, oldest first." />
        {records.loading ? (
          <Loading />
        ) : records.error ? (
          <ErrorState title="Could not load the wallet rows" message={records.error} onRetry={onRetry} compact />
        ) : moves.length === 0 ? (
          <EmptyState title="No wallet rows for this lesson." compact />
        ) : (
          <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none" }}>
            {moves.map((row, i) => {
              const move = walletMove(row, booking, nameOf);
              return (
                <Box
                  component="li"
                  key={row.id}
                  sx={{ display: "flex", alignItems: "baseline", gap: "12px", py: "12px", borderTop: i ? `1px solid ${tokens.divider}` : "none" }}
                >
                  <Box aria-hidden sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: tokens.success.main, flexShrink: 0 }} />
                  <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary, width: 128, flexShrink: 0 }}>
                    {at(row.createdAt) || "—"}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="bodySmall" sx={{ color: tokens.text.primary }}>
                      {move.title}
                    </Typography>
                    {move.sub && (
                      <Typography variant="labelMedium" component="div" sx={{ color: tokens.text.tertiary }}>
                        {move.sub}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Panel>
    </Box>
  );
};

// ── Rating & Feedback (D103) ──

const wordColor = (n) => (n >= 5 ? "success" : n >= 4 ? "info" : n >= 3 ? "warning" : "error");

// Five stars, the number and its word. The stars are drawn for the eye; the number is the text.
const Stars = ({ value, size = 22 }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
    <Box component="span" aria-hidden sx={{ display: "inline-flex", gap: "2px", color: tokens.star }}>
      {[1, 2, 3, 4, 5].map((i) => (i <= Math.round(value) ? <RiStarFill key={i} size={size} /> : <RiStarLine key={i} size={size} />))}
    </Box>
    <Typography variant={size > 16 ? "titleMetric" : "bodySmallStrong"} component="span" sx={{ color: tokens.text.primary }}>
      {ratingText(value)}
    </Typography>
    {ratingWord(value) && <Chip label={ratingWord(value)} color={wordColor(Math.round(value))} />}
  </Box>
);

// One side's rating: who rated, the stars, and their words.
const RatingCard = ({ title, line, rating, rater, emptyText, timeZone }) => (
  <Panel strong>
    <PanelHead
      title={title}
      line={line}
      aside={rating && toDate(rating.createdAt) && <Chip label={`Rated on ${dayLabel(rating.createdAt, { timeZone })}`} color="success" />}
    />
    {rating ? (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <InitialsAvatar name={rater.name} colorKey={rater.colorKey} size={40} square />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="titleCard" component="h3" sx={{ color: tokens.text.primary }}>
                {rater.name}
              </Typography>
              <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
                {rater.line}
              </Typography>
            </Box>
          </Box>
          <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
            Rating
          </Typography>
          <Stars value={rating.rating} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", minWidth: 0 }}>
          <Typography variant="bodySmallStrong" component="h3" sx={{ color: tokens.text.primary }}>
            Feedback
          </Typography>
          {rating.comment ? (
            <Quote text={rating.comment} />
          ) : (
            <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
              No written feedback.
            </Typography>
          )}
        </Box>
      </Box>
    ) : (
      <EmptyState title={emptyText} compact />
    )}
  </Panel>
);

export const RatingTab = ({ booking, records, users, childName, tutorRatings, timeZone, can, onRetry, onRetryOthers }) => {
  if (records.loading) return <Loading />;
  if (records.error) return <ErrorState title="Could not load the ratings" message={records.error} onRetry={onRetry} compact />;
  const { learner, tutor } = lessonRatings(records.ratings);
  const student = users[booking.studentId];
  const teacher = users[booking.teacherId];
  const child = childPath(booking);
  const emptyText = booking.status === "Cancelled" ? "No rating — the lesson was cancelled" : "Not rated yet";
  const learnerRater = {
    name: child ? childName || "Child" : personName(student) || "—",
    colorKey: child ? `${child.parentId}/${child.childId}` : booking.studentId,
    line: child ? `Parent: ${personName(student) || "—"}` : "Student",
  };
  const tutorRater = { name: personName(teacher) || "—", colorKey: booking.teacherId, line: "Tutor" };
  const others = otherRatings(tutorRatings.list, booking.id);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <RatingCard
        title="Learner's rating (for the tutor)"
        line="The family can rate the lesson once it is completed."
        rating={learner}
        rater={learnerRater}
        emptyText={emptyText}
        timeZone={timeZone}
      />
      <RatingCard
        title="Tutor's rating (of the learner)"
        line="The tutor rates the learner after the lesson."
        rating={tutor}
        rater={tutorRater}
        emptyText={emptyText}
        timeZone={timeZone}
      />
      <Panel strong>
        <PanelHead
          title="Previous ratings"
          line={`The latest ratings ${personName(teacher) || "this tutor"} got for other lessons.`}
          aside={
            can.tutors && (
              <SmallButton tone="outline" component={RouterLink} to={`/tutors/${booking.teacherId}/ratings`}>
                View all
              </SmallButton>
            )
          }
        />
        {tutorRatings.loading ? (
          <Loading />
        ) : tutorRatings.error ? (
          <ErrorState title="Could not load the tutor's ratings" message={tutorRatings.error} onRetry={onRetryOthers} compact />
        ) : others.length === 0 ? (
          <EmptyState title="No other ratings yet." compact />
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: "12px" }}>
            {others.map((r) => (
              <ButtonBase
                key={r.id}
                component={RouterLink}
                to={`/lessons/${r.lessonId}`}
                disabled={!r.lessonId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  p: "14px",
                  borderRadius: "12px",
                  border: `1px solid ${tokens.border.default}`,
                  textAlign: "left",
                  "&:hover": { bgcolor: tokens.hover },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 }}>
                  <Typography variant="labelMedium" sx={{ color: tokens.text.secondary }}>
                    {toDate(r.createdAt) ? dayLabel(r.createdAt, { timeZone }) : "—"}
                  </Typography>
                  <Stars value={r.rating} size={16} />
                </Box>
                <RiArrowRightSLine size={18} aria-hidden />
              </ButtonBase>
            ))}
          </Box>
        )}
      </Panel>
    </Box>
  );
};
